import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { OAuthProvider } from '@platform-frontend/account';
import { CheckoutLocale, CheckoutSession, RedirectHandling } from '@platform-frontend/checkout-drop-in';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { LocaleSetupService } from '@platform-frontend/transloco';
import { HslColor } from '@platform-frontend/ui-tools';
import { convertToHttpParams, handleSpecificErrors, PlannedError } from '@platform-frontend/util';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AppFlow } from '../flow';
import { SetCheckoutSessionRequired, VendorState } from '../vendor';
import { AccountMigration } from './model/account-migration';
import { CheckoutStyle } from './model/checkout-style';
import { StartupConfiguration } from './model/startup-configuration';
import { StartupConfigurationParams } from './model/startup-configuration-params';
import { StartupConfigurationStateModel } from './model/startup-configuration-state-model';
import { FetchAndHandleStartupConfiguration } from './startup-configuration.actions';

const DEFAULT_STATE: StartupConfigurationStateModel = {
  startupConfiguration: null
};

@State<StartupConfigurationStateModel>({
  name: 'startupConfiguration',
  defaults: DEFAULT_STATE
})
@Injectable()
export class StartupConfigurationState {
  constructor(private http: HttpClient, private localeSetupService: LocaleSetupService) {}

  static SESSION_TOKEN_SEARCH_AND_REPLACE_STRING = '$$SESSION_TOKEN$$';

  static getRedirectUrlWithCheckoutSession = (checkoutSession: CheckoutSession | null, redirectUrl?: string | null): string | null =>
    redirectUrl?.replace(StartupConfigurationState.SESSION_TOKEN_SEARCH_AND_REPLACE_STRING, checkoutSession?.sessionToken ?? 'null') ?? null;

  @Selector()
  static startupConfiguration(state: StartupConfigurationStateModel): StartupConfiguration | null {
    return state.startupConfiguration;
  }

  @Selector([StartupConfigurationState.startupConfiguration])
  static preferredLocale(state: StartupConfigurationStateModel, startupConfiguration: StartupConfiguration | null): CheckoutLocale | null {
    return startupConfiguration?.preferredLocale ?? null;
  }

  @Selector([StartupConfigurationState.startupConfiguration])
  static checkoutStyle(state: StartupConfigurationStateModel, startupConfiguration: StartupConfiguration | null): CheckoutStyle | null {
    return startupConfiguration?.checkoutStyle ?? null;
  }

  @Selector([StartupConfigurationState.checkoutStyle])
  static brandingColor(state: StartupConfigurationStateModel, checkoutStyle: CheckoutStyle | null): HslColor | null {
    return checkoutStyle?.brandingColor ?? null;
  }

  @Selector([StartupConfigurationState.startupConfiguration])
  static groupName(state: StartupConfigurationStateModel, startupConfiguration: StartupConfiguration | null): string | null {
    return startupConfiguration?.groupContext?.name ?? null;
  }

  @Selector([StartupConfigurationState.startupConfiguration])
  static vendorName(state: StartupConfigurationStateModel, startupConfiguration: StartupConfiguration | null): string | null {
    return startupConfiguration?.vendorName ?? null;
  }

  @Selector([StartupConfigurationState.startupConfiguration])
  static accountMigration(state: StartupConfigurationStateModel, startupConfiguration: StartupConfiguration | null): AccountMigration | null {
    return startupConfiguration?.accountMigration ?? null;
  }

  @Selector([StartupConfigurationState.startupConfiguration, VendorState.checkoutSession])
  static onCloseRedirectUrl(
    state: StartupConfigurationStateModel,
    startupConfiguration: StartupConfiguration | null,
    checkoutSession: CheckoutSession | null
  ): string | null {
    return StartupConfigurationState.getRedirectUrlWithCheckoutSession(checkoutSession, startupConfiguration?.onClose?.redirectUrl);
  }

  @Selector([StartupConfigurationState.startupConfiguration, VendorState.checkoutSession])
  static onSuccessRedirectUrl(
    state: StartupConfigurationStateModel,
    startupConfiguration: StartupConfiguration | null,
    checkoutSession: CheckoutSession | null
  ): string | null {
    return StartupConfigurationState.getRedirectUrlWithCheckoutSession(checkoutSession, startupConfiguration?.onSuccess?.redirectUrl);
  }

  @Selector([StartupConfigurationState.startupConfiguration])
  static orderedOAuthProviders(state: StartupConfigurationStateModel, startupConfiguration: StartupConfiguration | null): OAuthProvider[] {
    return startupConfiguration?.orderedOAuthProviders ?? [];
  }

  @Action(FetchAndHandleStartupConfiguration)
  fetchStartupConfiguration(ctx: StateContext<StartupConfigurationStateModel>, { appFlow }: FetchAndHandleStartupConfiguration): Observable<unknown> {
    const params: StartupConfigurationParams =
      appFlow.type === 'SIGN_IN'
        ? { vendorId: appFlow.startParams.vendorId }
        : {
            ...appFlow.startParams.productIdentifier
          };

    return this.http
      .get<StartupConfiguration>(PLATFORM_ENVIRONMENT.apiUrl + '/startup-configuration', {
        params: convertToHttpParams(params)
      })
      .pipe(
        map(startupConfiguration =>
          startupConfiguration.accountMigration?.launchDate
            ? {
                ...startupConfiguration,
                accountMigration: { ...startupConfiguration.accountMigration, launchDate: new Date(startupConfiguration.accountMigration.launchDate) }
              }
            : startupConfiguration
        ),
        tap(startupConfiguration => ctx.patchState({ startupConfiguration })),
        tap(startupConfiguration => this.setLocale(appFlow, startupConfiguration)),
        switchMap(startupConfiguration => this.fetchCheckoutSessionIfRequired(ctx, startupConfiguration)),
        handleSpecificErrors({ NOT_FOUND: () => throwError(() => new PlannedError('WRONG_STARTUP_CONFIGURATION_PARAMS')) })
      );
  }

  private setLocale(appFlow: AppFlow, startupConfiguration: StartupConfiguration): void {
    // Either set locale by frontend API...
    const locale = appFlow.startParams.locale;
    const preferredLocale = startupConfiguration.preferredLocale;
    if (locale) {
      this.localeSetupService.setLocaleByApi(locale);
    } else if (preferredLocale) {
      // ...or by backend configuration
      this.localeSetupService.setLocaleByApi(preferredLocale);
    }
  }

  private fetchCheckoutSessionIfRequired(ctx: StateContext<StartupConfigurationStateModel>, startupConfiguration: StartupConfiguration): Observable<unknown> {
    if (this.redirectHandlingRequiresToken(startupConfiguration.onClose) || this.redirectHandlingRequiresToken(startupConfiguration.onSuccess)) {
      return ctx.dispatch(new SetCheckoutSessionRequired());
    }
    return of(null);
  }

  private redirectHandlingRequiresToken(redirectHandling: RedirectHandling | null): boolean {
    return redirectHandling?.redirectUrl?.includes(StartupConfigurationState.SESSION_TOKEN_SEARCH_AND_REPLACE_STRING) ?? false;
  }
}
